import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Tooltip,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { Warehouse } from './warehouse';

interface WarehouseTableProps {
  warehouses: Warehouse[];
  onView: (warehouse: Warehouse) => void;
  onEdit: (warehouse: Warehouse) => void;
  onDelete: (warehouse: Warehouse) => void;
  deleteLoading: string;
  // ── NEW: passed from WarehousesPage so getSoftChipSx() selects the correct
  // colour palette for the current theme mode.
  //
  // Without this prop every chip uses the light-mode palette (dark text on
  // pale backgrounds). In dark mode those colours — e.g. '#b76e00' on a
  // nearly-black paper — have very low contrast and are almost invisible.
  //
  // The parent derives this value with:
  //   const isDark = theme.palette.mode === 'dark';
  // and passes it here so both the page and the table always agree.
  isDark?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

// ── Theme-aware chip style helper ─────────────────────────────────────────────
//
// Returns MUI `sx` props for a translucent, outlined chip.
//
// In light mode: soft pastel tints (low-opacity fills) + dark text.
// In dark mode:  richer semi-transparent fills + bright/light text so the chip
//                is clearly legible against dark paper backgrounds.
//
// Pass `isDark` (from `theme.palette.mode === 'dark'` in the parent) to switch
// between the two palettes.
// ─────────────────────────────────────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  // Light mode: subtle tints — text must be dark enough for WCAG contrast on
  // white/light-grey paper.
  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  // Dark mode: higher-opacity fills so the tint is visible on dark surfaces;
  // text shifted to light/vivid variants so chips pop without being garish.
  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const WarehouseTable: React.FC<WarehouseTableProps> = ({
  warehouses,
  onView,
  onEdit,
  onDelete,
  deleteLoading,
  isDark = false,
  canEdit = false,
  canDelete = false,
}) => {
  const formatDate = (date: Date | undefined) => {
    if (!date) return '-';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getUtilizationColor = (
    utilization: number
  ): 'error' | 'warning' | 'success' => {
    if (utilization >= 90) return 'error';
    if (utilization >= 75) return 'warning';
    return 'success';
  };

  return (
    // ── height: '100%' is the key to full-height table behaviour ─────────────
    //
    // The parent Box in WarehousesPage has:
    //   flex: 1  +  minHeight: { xs: 'auto', md: 0 }
    //
    // That means on md+ the Box stretches to fill all remaining vertical space
    // after the fixed header / pagination. Setting height:'100%' on this
    // TableContainer makes it fill that Box completely — so even with only
    // 1–2 rows the table occupies the full available area, matching the
    // ReturnsManagement pattern.
    //
    // Without height:'100%' the TableContainer wraps its content and the table
    // collapses to the height of its rows, leaving an ugly gap below.
    // ─────────────────────────────────────────────────────────────────────────
    <TableContainer
      component={Paper}
      sx={{
        height: '100%',
        '& .MuiTable-root': { minWidth: 1000 },
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Warehouse</TableCell>
            <TableCell>Code</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Capacity</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {warehouses.map((warehouse) => {
            const utilization =
              warehouse.capacity.total > 0
                ? (warehouse.capacity.used / warehouse.capacity.total) * 100
                : 0;

            return (
              <TableRow
                key={warehouse.id}
                hover
                sx={{
                  '&:hover': { backgroundColor: 'action.hover' },
                  '&:last-child td, &:last-child th': { borderBottom: 0 },
                }}
              >
                {/* ── Warehouse name & description ── */}
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {warehouse.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ maxWidth: 200 }}
                    >
                      {warehouse.description}
                    </Typography>
                  </Box>
                </TableCell>

                {/* ── Code chip — isDark switches to vivid-on-dark palette ── */}
                <TableCell>
                  <Chip
                    label={warehouse.code}
                    size="small"
                    variant="outlined"
                    sx={getSoftChipSx('primary', isDark)}
                  />
                </TableCell>

                {/* ── Location ── */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="body2">
                        {warehouse.address.city}
                        {warehouse.address.state
                          ? `, ${warehouse.address.state}`
                          : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {warehouse.address.country}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* ── Contact ── */}
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {warehouse.contact.manager || 'Not assigned'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {warehouse.contact.phone || 'No phone'}
                    </Typography>
                  </Box>
                </TableCell>

                {/* ── Capacity bar ── */}
                <TableCell>
                  <Box sx={{ minWidth: 120 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {utilization.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {warehouse.capacity.used.toLocaleString()}/
                        {warehouse.capacity.total.toLocaleString()}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(utilization, 100)}
                      color={getUtilizationColor(utilization)}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                </TableCell>

                {/* ── Status chip — isDark switches to vivid-on-dark palette ── */}
                <TableCell>
                  <Chip
                    label={warehouse.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    variant="outlined"
                    sx={getSoftChipSx(
                      warehouse.isActive ? 'success' : 'default',
                      isDark
                    )}
                  />
                </TableCell>

                {/* ── Created date ── */}
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(warehouse.createdAt)}
                  </Typography>
                </TableCell>

                {/* ── Actions ── */}
                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    justifyContent="flex-end"
                    flexWrap="nowrap"
                  >
                    <Tooltip title="View Warehouse">
                      <IconButton
                        onClick={() => onView(warehouse)}
                        size="small"
                        color="primary"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canEdit && (
                      <Tooltip title="Edit Warehouse">
                        <IconButton onClick={() => onEdit(warehouse)} size="small" color="warning">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Delete Warehouse">
                        <IconButton
                          onClick={() => onDelete(warehouse)}
                          disabled={deleteLoading === warehouse.id}
                          size="small"
                          color="error"
                        >
                          {deleteLoading === warehouse.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default WarehouseTable;